import type {
  ColDef,
  ICellRendererParams,
  ValueFormatterParams,
} from 'ag-grid-community';
import { DateTime } from 'luxon';
import { type FC, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { EyeFill } from 'react-bootstrap-icons';
import { UserPropertiesContext } from '../../modules/context/userPropertiesContext';
import { ReservationState } from '../../modules/enums/types';
import { useAuth, useLoading, useRequest } from '../../modules/hooks';
import type { MewsOrderItem, Reservations } from '../../modules/interfaces';
import { endpoints } from '../../modules/mappers/urls';
import { numberSort, roomSort } from '../../modules/utils/tableSorts';
import { DataTable } from '../Tables/DataTable';
import { AccessCodeModal } from './ReservationModals/AccessCodeModal';

interface StartedReservationsModalProps {
  show: boolean;
  handleClose: () => void;
  reservations: Reservations[];
}

export const StartedReservationsModal: FC<StartedReservationsModalProps> = ({
  show,
  handleClose,
  reservations,
}) => {
  const [accessCodeModalOpen, setAccessCodeModalOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState<Reservations | null>(
    null,
  );
  const [accessCode, setAccessCode] = useState('');
  const [accessCodeError, setAccessCodeError] = useState<string | null>(null);
  const [pendingAccessCode, setPendingAccessCode] = useState(false);

  const { credentialsInfo } = useAuth()!;
  const { selectedProperty } = useContext(UserPropertiesContext)!;
  const { setLoading } = useLoading()!;

  const getAccessCodeErrorMessage = (message?: string) => {
    const errorPrefix = 'Unable to obtain reservation access code due to:';
    if (!message) {
      return 'Could not retrieve access code at this time. Please try again later.';
    }

    return message.startsWith(errorPrefix)
      ? message.slice(errorPrefix.length).trim()
      : message;
  };

  const accessCodeUrl = selectedReservation
    ? `${endpoints.RESERVATION}/reservation-code/${selectedReservation.Id}`
    : `${endpoints.RESERVATION}/reservation-code/`;

  const [
    {
      data: obtainReservationCode,
      loading: loadingObtainReservationCode,
      error: errorObtainReservationCode,
    },
    obtainAccessCode,
  ] = useRequest<string>(
    accessCodeUrl,
    'get',
    {
      authToken: credentialsInfo?.token,
      propertyId: selectedProperty?.property?.uuid,
    },
    { manual: true },
  );

  const startedReservations = useMemo(
    () =>
      reservations.filter(
        (reservation) => reservation.State === ReservationState.STARTED,
      ),
    [reservations],
  );

  const actionCellRenderer = ({
    data,
  }: ICellRendererParams<Reservations, unknown>): ReactNode => {
    return (
      <Button
        className="save-button access-code-trigger-button"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        aria-label="Show access code"
        title="Show access code"
        onClick={(event) => {
          event.stopPropagation();
          handleShowAccessCode(data ?? null);
        }}
      >
        <EyeFill size={16} color="#ffffff" />
      </Button>
    );
  };

  const columns: ColDef<Reservations>[] = useMemo(() => {
    const baseColumns: ColDef<Reservations>[] = [
      {
        valueGetter: (params) => {
          const { data: reservation } = params;
          const resource = reservation?.Resource;
          const name = resource
            ? `${resource.Name} -  ${resource.ResourceName}`
            : 'Not assigned';
          return name;
        },
        headerName: 'ROOM',
        sortable: true,
        comparator: (valueA, valueB) => roomSort(valueA, valueB),
        flex: 1,
      },
      {
        valueGetter: (params) => {
          const { data: reservation } = params;
          const customer = reservation?.Customer;
          const name = customer ? `${customer.FirstName} ${customer.LastName}` : '-';
          return name;
        },
        headerName: 'GUEST NAME',
        sortable: true,
        flex: 1,
      },
      {
        field: 'ScheduledStartUtc',
        headerName: 'ARRIVAL DATE',
        sortable: true,
        valueFormatter: (params: ValueFormatterParams<Reservations, string>) => {
          const { value } = params;
          return DateTime.fromISO(value || '').toFormat('dd LLL yyyy');
        },
        cellStyle: { color: '#757474' },
        flex: 1,
      },
      {
        field: 'EndUtc',
        headerName: 'DEPARTURE DATE',
        sortable: true,
        valueFormatter: (params: ValueFormatterParams<Reservations, string>) => {
          const { value } = params;
          return DateTime.fromISO(value || '').toFormat('dd LLL yyyy');
        },
        cellStyle: { color: '#757474' },
        flex: 1,
      },
      {
        field: 'OrderItem',
        headerName: 'AMOUNT',
        sortable: true,
        comparator: (valueA, valueB) =>
          numberSort(valueA.Amount.NetValue, valueB.Amount.NetValue),
        valueFormatter: (params: ValueFormatterParams<Reservations, MewsOrderItem>) => {
          const { value } = params;
          return (
            value?.Amount?.NetValue?.toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
            }) || `$0.00`
          );
        },
        flex: 1,
      },
      {
        colId: 'action',
        headerName: 'ACTIONS',
        cellRenderer: actionCellRenderer,
        sortable: false,
        width: 120,
        cellClass: 'actionCol',
        cellStyle: { display: 'flex', justifyContent: 'center', alignItems: 'center' },
      },
    ];

    return baseColumns;
  }, []);

  useEffect(() => {
    setLoading(loadingObtainReservationCode);
  }, [loadingObtainReservationCode, setLoading]);

  useEffect(() => {
    if (!obtainReservationCode?.result) return;
    const currentAccessCode = `${obtainReservationCode.result}#`;
    setAccessCode(currentAccessCode);
    setAccessCodeError(null);
    setAccessCodeModalOpen(true);
  }, [obtainReservationCode]);

  useEffect(() => {
    if (errorObtainReservationCode) {
      const rawErrorMessage = errorObtainReservationCode.response?.data?.error?.message;
      setAccessCodeError(getAccessCodeErrorMessage(rawErrorMessage));
      setAccessCodeModalOpen(true);
    }
  }, [errorObtainReservationCode]);

  useEffect(() => {
    if (!pendingAccessCode || !selectedReservation) return;

    obtainAccessCode({
      headers: {
        authorization: credentialsInfo?.token,
        'property-id': selectedProperty?.property?.uuid,
      },
    });
    setPendingAccessCode(false);
  }, [
    pendingAccessCode,
    selectedReservation,
    obtainAccessCode,
    credentialsInfo?.token,
    selectedProperty?.property?.uuid,
  ]);

  const handleAccessCodeModalClose = () => {
    setAccessCodeModalOpen(false);
    setAccessCode('');
    setAccessCodeError(null);
  };

  const closeAllModals = () => {
    handleAccessCodeModalClose();
    setSelectedReservation(null);
    setPendingAccessCode(false);
    handleClose();
  };

  const handleShowAccessCode = (reservation: Reservations | null) => {
    setSelectedReservation(reservation);
    setAccessCodeError(null);
    setPendingAccessCode(!!reservation);
  };

  return (
    <>
      <Modal
        show={show}
        onHide={closeAllModals}
        dialogClassName="post-editor-modal modal-1100w"
        backdrop="static"
        centered
        size="xl"
      >
        <Modal.Header>
          <Modal.Title className="editor-title">Reservations</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <DataTable
            rowData={startedReservations}
            columnDefs={columns}
            changeState={0}
            pagination
            paginationPageSize={5}
            component="reservations"
            showSearchInput={false}
          />
        </Modal.Body>
        <Modal.Footer
          style={{
            display: 'flex',
            justifyContent: 'end',
            marginTop: '8px',
          }}
        >
          <Button className="cancel-button" onClick={closeAllModals}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <AccessCodeModal
        show={accessCodeModalOpen}
        handleClose={handleAccessCodeModalClose}
        accessCode={accessCode}
        errorMessage={accessCodeError}
      />
    </>
  );
};
